import * as React from "react";
import _ from "lodash";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";

import { ITableProps } from "src/library/components/Tables/Table";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Text } from "src/library/components/Text";
import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { IUser } from "src/core/users/api/users.api";
import { ICustomer } from "src/core/customers/api/customers.api";
import Status from "src/library/components/Status/Status";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { formatCustomerName } from "src/core/customers/utils/format-customer-name";

export interface IUserTableColumns extends Partial<ITableProps> {
  actionButton: (info: CellContext<IUser, unknown>) => React.ReactNode;
  terminology: ITerminology;
  removeColumns?: UserAvailableTableColumn[];
}

export type UserAvailableTableColumn = keyof AvailableUserColumns;

interface AvailableUserColumns {
  email: ColumnDef<IUser, string>;
  role: ColumnDef<IUser, string>;
  firstName: ColumnDef<IUser, string>;
  lastName: ColumnDef<IUser, string>;
  status: ColumnDef<IUser, string>;
  lastLoggedIn: ColumnDef<IUser, Date>;
  defaultDrop: ColumnDef<IUser, string>;
  defaultCustomer: ColumnDef<IUser, ICustomer>;
  actions: ColumnDef<IUser, unknown>;
}

export const getUserTableColumns = (params: IUserTableColumns): Array<ColumnDef<IUser, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IUser>();
  const columnHelper = createColumnHelper<IUser>();
  const UserStatuses = [ "active", "suspended" ];

  const availableColumns: AvailableUserColumns = {
    email: customColumnHelper.standardTextColumn(columnHelper, "email", {
      header: "Email",
      size: 200
    }),
    role: customColumnHelper.standardTextColumn(columnHelper, "role.name", {
      header: "Role",
      enableSorting: false
    }),
    firstName: customColumnHelper.standardTextColumn(columnHelper, "firstName", { header: "First Name" }),
    lastName: customColumnHelper.standardTextColumn(columnHelper, "lastName", { header: "Last Name" }),
    status: customColumnHelper.standardTextColumn(columnHelper, "status", {
      header: "Status",
      cell: info => {
        if (info.row.original.status) {
          return (
            <Status
              status={info.row.original.status as "active"| "suspended"}
            />
          );
        }

        return;
      },
      meta: {
        filter: {
          filterType: "select",
          options: UserStatuses.map(status => {
            return {
              text: _.capitalize(status),
              value: status
            };
          })
        }
      }
    }),
    lastLoggedIn: customColumnHelper.dateColumn(columnHelper, "lastLoggedIn", {
      header: "Last Logged In",
      enableColumnFilter: true,
      enableSorting: false
    }),
    defaultDrop: customColumnHelper.standardTextColumn(columnHelper, "userBranches", {
      header: `Default ${params.terminology.drop.singular.text}`,
      minSize: 150,
      cell: info => {
        const userBranches = info.row.original.userBranches;
        const defaultBranchId = info.row.original.defaultBranchId;
        const defaultBranch = userBranches?.find(ub => ub.id === defaultBranchId);

        if (defaultBranch) {
          return (
            <Text>
              {formatDropName(defaultBranch)}
            </Text>
          );
        }

        return;
      },
      enableSorting: false,
      enableColumnFilter: false
    }),
    defaultCustomer: customColumnHelper.standardTextColumn(columnHelper, "userCustomers", {
      header: `Default ${params.terminology.customer.singular.text}`,
      minSize: 150,
      cell: info => {
        const userCustomers = info.row.original.userCustomers;
        const defaultCustomerId = info.row.original.defaultCustomerId;
        const defaultCustomer = userCustomers?.find(ub => ub.id === defaultCustomerId);

        if (defaultCustomer) {
          return (
            <Text>
              {formatCustomerName(defaultCustomer)}
            </Text>
          );
        }

        return;
      },
      enableSorting: false,
      enableColumnFilter: false
    }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})
  };

  if (params.removeColumns) {
    for (const col of params.removeColumns) {
      delete availableColumns[ col ];
    }
  }
  const columns = Object.values(availableColumns);

  return columns;
};
